import axios from 'axios';
import Cookies from 'js-cookie';
import { createVpayAccount } from '@vezeeta/vpay-pack';
import { Cookie } from 'constants/appCookies';
import { parseJwt } from 'utils/functions/authentication';
import { mapBanks, mapFinancialInfoAccount, mapBankAccountPayload } from './bank-account.adapter';
import { mapTransactionsPage } from './transactions.adapter';
import { mapTransfersPage, mapTransferDetail } from './transfers.adapter';
import { formatUTCDateToReadable as formatDate } from './date';

const baseURL = process.env.REACT_APP_VPAY_API_URL;

export const createVpayApi = (userKey, culture, profileEmail) => {
  const storageKey = `VZT_IMS_VPAY_${userKey}_${culture}`;
  let tokens;
  try {
    tokens = JSON.parse(sessionStorage.getItem(storageKey)) || {};
  } catch {
    tokens = {};
  }
  if (tokens.portalToken !== Cookies.get(Cookie.AUTH_TOKEN)) tokens = {};
  let accountKey;
  let email = profileEmail;
  let refreshing;
  const client = axios.create({ baseURL });
  const hostHeaders = () => ({
    'x-vzt-authentication': Cookies.get(Cookie.AUTH_TOKEN),
    'x-vzt-component': 'VPS',
    'X-Culture': culture,
    'x-vzt-Culture': culture,
  });
  const persist = result => {
    if (!result?.accessToken) throw new Error('Missing VPay access token');
    tokens = {
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      portalToken: Cookies.get(Cookie.AUTH_TOKEN),
    };
    sessionStorage.setItem(storageKey, JSON.stringify(tokens));
    return result;
  };
  const clear = () => {
    tokens = {};
    sessionStorage.removeItem(storageKey);
  };
  const unwrap = response => {
    if (response.data?.isSuccess === false) {
      throw new Error(response.data.message || 'VPay request failed');
    }
    return response.data?.data;
  };
  const authenticate = async (payload = {}) => {
    const response = await axios.post(`${baseURL}/authentication`, payload, {
      headers: hostHeaders(),
    });
    return persist(unwrap(response));
  };
  const refresh = () => {
    if (!refreshing) {
      refreshing = axios
        .post(
          `${baseURL}/authentication/token/refresh`,
          {
            refreshToken: tokens.refreshToken,
          },
          { headers: { 'X-Culture': culture } },
        )
        .then(response => persist(unwrap(response)))
        .catch(error => {
          clear();
          throw error;
        })
        .finally(() => {
          refreshing = null;
        });
    }
    return refreshing;
  };
  client.interceptors.request.use(config => ({
    ...config,
    headers: {
      ...config.headers,
      Authorization: tokens.accessToken,
      'x-vzt-component': 'VPS',
      'X-Culture': culture,
      'x-vzt-Culture': culture,
    },
  }));
  client.interceptors.response.use(
    response => response,
    async error => {
      const request = error.config;
      if (error.response?.status !== 401) throw error;
      const expired = () => {
        clear();
        const destination = window.location.pathname.replace(/^\/vpay/, '') || '/';
        window.location.assign(`/vpay/login?redirect=${encodeURIComponent(destination)}`);
      };
      if (request?.vpayRetried || !tokens.refreshToken) {
        expired();
        throw error;
      }
      request.vpayRetried = true;
      try {
        await refresh();
      } catch (refreshError) {
        expired();
        throw refreshError;
      }
      return client(request);
    },
  );
  const post = async (path, payload) => unwrap(await client.post(path, payload));
  const login = async (passcode, save = true) => {
    const response = await axios.post(
      `${baseURL}/authentication/login?userTypeId=3`,
      {
        emailAddress: email,
        passcode,
      },
      { headers: { 'X-Culture': culture, 'x-vzt-component': 'VPS' } },
    );
    const result = unwrap(response);
    return save ? persist(result) : result;
  };
  return {
    async getAccount() {
      if (!baseURL) throw new Error('Configure REACT_APP_VPAY_API_URL for this environment.');
      let customer;
      try {
        customer = unwrap(
          await axios.get(`${baseURL}/customers/external`, { headers: hostHeaders() }),
        );
      } catch (error) {
        if (error.response?.status !== 404) throw error;
        clear();
        return createVpayAccount({ exists: false });
      }
      const account = customer.accounts?.find(
        item => item.countryISOCode === culture.split('-')[1],
      );
      if (!account?.accountKey) throw new Error('No VPay account for the selected country.');
      accountKey = account.accountKey;
      email = customer.emailAddress || customer.email || profileEmail;
      const claims = parseJwt(tokens.accessToken);
      // The external account lookup is read-only; authenticate an existing customer once per session.
      if (!Number.isFinite(claims.exp) || claims.exp * 1000 <= Date.now()) {
        if (tokens.refreshToken) {
          try {
            await refresh();
          } catch {
            await authenticate();
          }
        } else {
          await authenticate();
        }
      }
      const currentClaims = parseJwt(tokens.accessToken);
      email = email || currentClaims.email;
      return {
        ...createVpayAccount({
          exists: true,
          verified: customer.verified,
          financialDataComplete: customer.requiredFinancialDataCompleted,
          passcodeEnabled: currentClaims.passcodeEnabled,
          authenticatedByPasscode: currentClaims.authenticatedByPassCode,
        }),
        email,
        totalBalance: account.totalBalance,
        currency: account.currency,
      };
    },
    authenticate,
    requestOtp: () => client.post('/authentication/otp/request'),
    async verifyOtp(code) {
      const result = await post('/authentication/otp/validate', { code });
      // Reset tokens have a separate purpose and must never replace the dashboard session.
      if (parseJwt(result?.accessToken).purpose !== 'reset-passcode') persist(result);
      return result;
    },
    async getBank() {
      const { id } = parseJwt(tokens.accessToken);
      if (!id) throw new Error('Missing VPay customer');
      const response = await client.get(`/customers/${encodeURIComponent(id)}/financial-info`);
      unwrap(response);
      return mapFinancialInfoAccount(response.data);
    },
    async getBanks() {
      const response = await axios.get(`${process.env.REACT_APP_STATIC_URL}/api/banks`, {
        headers: { CountryId: culture.endsWith('-SA') ? 2 : 1 },
      });
      return mapBanks(response.data?.Data);
    },
    saveBank: account => authenticate(mapBankAccountPayload(account)),
    login: code => login(code),
    checkPasscode: code => login(code, false),
    async createPasscode(passcode) {
      return persist(await post('/authentication/passcode', { passcode }));
    },
    changePasscode: (passcode, newPasscode) =>
      post('/authentication/passcode/change', { passcode, newPasscode }),
    async resetPasscode(passcode, accessToken) {
      const response = await axios.post(
        `${baseURL}/authentication/passcode/reset`,
        { passcode },
        {
          headers: { Authorization: accessToken, 'X-Culture': culture },
        },
      );
      return persist(unwrap(response));
    },
    async getTransactions({ page, direction, fromDate, toDate }) {
      const response = await client.get(
        `/v2/accounts/${encodeURIComponent(accountKey)}/transactions`,
        {
          params: {
            page,
            pageSize: 10,
            direction,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
          },
        },
      );
      return mapTransactionsPage(response.data, formatDate);
    },
    async getTransfers({ page, displayStatus, fromDate, toDate }) {
      const response = await client.get(
        `/accounts/${encodeURIComponent(accountKey)}/payout-transfers`,
        {
          params: {
            pageNumber: page,
            pageSize: 10,
            displayStatus: displayStatus || undefined,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
          },
        },
      );
      return mapTransfersPage(response.data, { page, pageSize: 10, formatDate });
    },
    async getTransfer(id) {
      const response = await client.get(`/payouttransfers/${encodeURIComponent(id)}/detail`);
      return mapTransferDetail(response.data, formatDate);
    },
  };
};
